import clsx from 'clsx';
import { NavLink } from 'react-router-dom';

import useRenttrack from '../../services/useRenttrack';
import { TWithGlobals } from '../../withGlobals';
import TenantPhoneLink from '../TenantPhoneLink';

import classes from './NavBar.module.css';
import NavFooter from './NavFooter';
import { TNavMenuItem } from './NavMenuItem';
import NavMenuList, { TMenuLink } from './NavMenuList';

export type TNavBarMenu = Pick<TNavMenuItem, 'onToggleMenu'> &
  Partial<
    Pick<
      TWithGlobals,
      'isCCPABannerOpen' | 'isLoggedIn' | 'oktaAccessToken' | 'oktaIdToken'
    >
  >;

function NavBarMenu({
  isLoggedIn = false,
  onToggleMenu,
  oktaAccessToken,
  oktaIdToken,
}: TNavBarMenu) {
  const { hasLevelCreditAccount, levelCreditRedirectUrl } = useRenttrack(
    oktaIdToken,
    oktaAccessToken
  );

  const claimsHref = process.env.REACT_APP_CLAIMS_URL!;

  const loggedInMenuLinks: TMenuLink[] = [
    {
      to: '/my-account',
      title: 'My policies',
      dataTestId: 'NavBarMenu_link_policies',
    },
    {
      to: '/my-activity',
      title: 'My payments',
      dataTestId: 'NavBarMenu_link_payments',
    },
    {
      to: '/my-profile',
      title: 'My profile',
      dataTestId: 'NavBarMenu_link_profile',
    },
    { to: claimsHref, title: 'Claims', dataTestId: 'NavBarMenu_link_claims' },
    {
      to: 'https://help.gettoggle.com/hc/en-us',
      title: 'FAQs',
      dataTestId: 'NavBarMenu_link_faqs',
    },
    ...(hasLevelCreditAccount
      ? [
          {
            to: levelCreditRedirectUrl,
            title: 'Credit Lift',
            dataTestId: 'credit_lift_nav_link',
          },
        ]
      : []),
  ];

  const loggedOutMenuLinks: TMenuLink[] = [
    {
      to: '/',
      title: 'Renters',
      dataTestId: 'NavBarMenu_link_renters',
      exact: true,
    },
    { to: '/auto', title: 'Auto', dataTestId: 'NavBarMenu_link_auto' },
    { to: claimsHref, title: 'Claims', dataTestId: 'NavBarMenu_link_claims' },
    {
      to: '',
      isDropdownMenu: true,
      dropdownLinks: [
        {
          to: 'https://help.gettoggle.com/hc/en-us',
          title: 'FAQs',
          dataTestId: 'NavBarMenu_sublink_faqs',
        },
        {
          to: 'https://petsbest.com/lp/toggle-pet-insurance',
          title: 'Pet insurance',
          dataTestId: 'NavBarMenu_sublink_pet',
        },
        {
          to: process.env.REACT_APP_BLOG_URL!,
          title: 'Blog',
          dataTestId: 'NavBarMenu_sublink_blog',
        },
        {
          to: process.env.REACT_APP_CONTACT_URL!,
          title: 'Contact Us',
          dataTestId: 'NavBarMenu_sublink_contact',
        },
        {
          to: '/about-us',
          title: 'About Us',
          dataTestId: 'NavBarMenu_sublink_about',
        },
      ],
      title: 'More',
      dataTestId: 'NavBarMenu_link_more',
    },
  ];

  const loggedInFooterLinks: TMenuLink[] = [
    { to: '/about-us', title: 'About Us' },
    { to: '/terms', title: 'Terms' },
    { to: '/privacy', title: 'Privacy' },
  ];

  const loggedOutFooterLinks: TMenuLink[] = [
    { to: '/terms', title: 'Terms' },
    { to: '/privacy', title: 'Privacy' },
  ];

  const menuLinks = isLoggedIn ? loggedInMenuLinks : loggedOutMenuLinks;
  const footerLinks = isLoggedIn ? loggedInFooterLinks : loggedOutFooterLinks;

  const loginLogoutLink = isLoggedIn ? '/logout' : '/login';
  const loginLogoutText = isLoggedIn ? 'Log out' : 'Log In';

  return (
    <div className={classes.menu}>
      <NavMenuList onToggleMenu={onToggleMenu} menuLinks={menuLinks} />
      <TenantPhoneLink className={classes.tenantPhone} />
      <NavLink
        to={loginLogoutLink}
        className={clsx(
          classes.logout,
          'btn',
          'btn-secondary',
          'btn-assertive'
        )}
      >
        {loginLogoutText}
      </NavLink>
      <NavFooter footerLinks={footerLinks} />
    </div>
  );
}

export default NavBarMenu;
